import { useState, Fragment } from 'react';
import type { FC } from 'react';
import { Container, Text, InfoIcon, Span } from './TextBlock.styles';
import SafeHTML from '@utils/safeHTML';
import { DefaultModal } from '@components/Modals/DefaultModal/DefaultModal';
import { useAppSelector } from '@app/hooks';
import { replaceTemplateVariables } from '@utils/replaceTemplateVariables';

export enum TypeOfText {
  H1,
  H2,
  H3,
  H4,
  Subtitle,
  Text,
}
export interface TextBlockProps {
  text?: string;
  direction?: string;
  color?: 'primary' | 'default' | 'textPrimary' | 'textSecondary';
  width?: string;
  typeModal?: string;
  isInfoModal?: boolean;
  isLinkModal?: boolean;
  type?: TypeOfText;
  infoModal?: {
    header?: string;
    body?: string;
    button?: string;
  };
  isHidden?: boolean;
  isCustomColor?: boolean;
  customColor?: string;
  infoIconColor?: string;
  isUnderline?: boolean;
  isPointer?: boolean;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  link?: string;
  disabled?: boolean;
}

export const TextBlock: FC<TextBlockProps> = ({
  text = '',
  direction = 'flex-start',
  color = 'default',
  type = TypeOfText.H1,
  width = '100%',
  isInfoModal = false,
  infoModal = {},
  isLinkModal = false,
  isHidden = false,
  isCustomColor = false,
  customColor = '#000000',
  infoIconColor,
  isUnderline = false,
  isPointer = false,
  textAlign = 'left',
  link,
  disabled = false,
}) => {
  const isReadEConcent = /%Read eConsent%/g.test(text);
  const [openModal, setOpenModal] = useState(false);
  const hasModal = isInfoModal || isLinkModal || isReadEConcent;
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const handleModal = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenModal(true);
  };

  // Replace %Read eConsent% with a placeholder before processing other template variables
  // This allows us to replace it with a clickable link later
  const textWithPlaceholder = text?.replace(/%Read eConsent%/g, '{{READ_ECONSENT_LINK}}') || '';
  let interpolatedText = replaceTemplateVariables(textWithPlaceholder, templateConfig);

  // Split text by placeholder to insert React component for the link
  const textParts = interpolatedText.split('{{READ_ECONSENT_LINK}}');

  return (
    <Container $direction={direction} data-testid="text-block-container" width={width} $isHidden={isHidden} $isPointer={isPointer}>
      <Text
        color={color}
        type={type}
        data-testid="text-block-text"
        $isCustomColor={isCustomColor}
        $customColor={customColor}
        $isUnderline={isUnderline}
        $isLinkModal={isLinkModal}
        $textAlign={textAlign}
        $disabled={disabled}
      >
        {isLinkModal ? (
          <Span $isCustomColor={isCustomColor} $customColor={customColor} color={color} style={{ display: 'inline' }}>
            <SafeHTML tag="a" html={interpolatedText.replace(/\n/g, '<br/>')} onClick={handleModal} />
            {isInfoModal && <InfoIcon data-testid="info-icon" onClick={handleModal} $infoIconColor={infoIconColor} />}
          </Span>
        ) : (
          <>
            {!link && (
              <Span $isCustomColor={isCustomColor} $customColor={customColor} color={color} style={{ display: 'inline' }}>
                {isReadEConcent && textParts.length > 1 ? (
                  <>
                    {textParts.map((part, index) => (
                      <Fragment key={index}>
                        <SafeHTML tag="span" html={part.replace(/\n/g, '<br/>')} />
                        {index < textParts.length - 1 && (
                          <a onClick={handleModal} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                            Read eConsent
                          </a>
                        )}
                      </Fragment>
                    ))}
                  </>
                ) : (
                  <SafeHTML tag="span" html={interpolatedText.replace(/\n/g, '<br/>')} />
                )}
                {isInfoModal && <InfoIcon data-testid="info-icon" onClick={handleModal} $infoIconColor={infoIconColor} />}
              </Span>
            )}
            {link && (
              <Span $isCustomColor={isCustomColor} $customColor={customColor} color={color} style={{ display: 'inline' }}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {text}
                </a>
                {isInfoModal && <InfoIcon data-testid="info-icon" onClick={handleModal} $infoIconColor={infoIconColor} />}
              </Span>
            )}
          </>
        )}
      </Text>
      {openModal && hasModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <DefaultModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            infoModal={infoModal}
            data-testid="modal"
            typeModal="default"
            isErrorModal={false}
            flexDirectionContent={'column'}
            showCloseButton={isInfoModal || isLinkModal || isReadEConcent}
          />
        </div>
      )}
    </Container>
  );
};
